// src/services/watchServices.js
import realTimeDataService from "./realTimeDataService";

const baseTokens = {
  BTC: { price: 42000, volatility: 0.02, name: "Bitcoin" },
  ETH: { price: 2800, volatility: 0.025, name: "Ethereum" },
  SOL: { price: 120, volatility: 0.04, name: "Solana" },
  AVAX: { price: 35, volatility: 0.035, name: "Avalanche" },
  LINK: { price: 18, volatility: 0.03, name: "Chainlink" },
  MATIC: { price: 1.2, volatility: 0.045, name: "Polygon" },
  UNI: { price: 7.5, volatility: 0.038, name: "Uniswap" },
  DOGE: { price: 0.08, volatility: 0.06, name: "Dogecoin" },
  ADA: { price: 0.5, volatility: 0.035, name: "Cardano" },
};

// Helper functions
const generateWalletAddress = () => {
  return `0x${Array.from({ length: 40 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("")}`;
};

const formatWalletAddress = (address) => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const formatDuration = (milliseconds) => {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

const formatPlayByPlayTime = (minutesAgo) => {
  if (minutesAgo < 1) return "just now";
  if (minutesAgo < 60) return `${minutesAgo} min ago`;
  const hours = Math.floor(minutesAgo / 60);
  const remainingMinutes = minutesAgo % 60;
  if (remainingMinutes === 0) {
    return `${hours}h ago`;
  }
  return `${hours}h ${remainingMinutes}m ago`;
};

// Play-by-play event generation
const generatePlayByPlayEvent = (raceData, minutesAgo) => {
  const eventTypes = [
    "position_change",
    "momentum_shift",
    "volume_spike",
    "milestone",
    "support_level",
    "new_participant",
  ];

  const eventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
  const tokens = raceData.tokens;

  const getRandomToken = () =>
    tokens[Math.floor(Math.random() * tokens.length)];
  const getTokenPair = () => {
    const first = getRandomToken();
    let second;
    do {
      second = getRandomToken();
    } while (second.symbol === first.symbol);
    return [first, second];
  };

  const baseEvent = {
    timestamp: minutesAgo,
    formattedTime: formatPlayByPlayTime(minutesAgo),
    importance: Math.random() > 0.7 ? "high" : "medium",
  };

  switch (eventType) {
    case "position_change": {
      const [token1, token2] = getTokenPair();
      const percentageDiff = Math.abs(
        token1.rawPercentChange - token2.rawPercentChange
      ).toFixed(2);
      return {
        ...baseEvent,
        type: "position_change",
        tokens: [token1.symbol, token2.symbol],
        description: `${token1.symbol} ${
          token1.rawPercentChange > token2.rawPercentChange // Changed this line
            ? "overtakes"
            : "falls behind"
        } ${token2.symbol} with ${percentageDiff}% difference`,
        data: {
          token1: {
            symbol: token1.symbol,
            percentChange: token1.percentChange,
            position: token1.position,
          },
          token2: {
            symbol: token2.symbol,
            percentChange: token2.percentChange,
            position: token2.position,
          },
        },
      };
    }

    case "momentum_shift": {
      const token = getRandomToken();
      const momentum = (Math.random() * 2 + 1).toFixed(2);
      return {
        ...baseEvent,
        type: "momentum_shift",
        tokens: [token.symbol],
        description: `${token.symbol} shows ${momentum}x momentum increase in buying pressure`,
        data: {
          momentum: parseFloat(momentum),
          percentChange: token.percentChange,
        },
      };
    }

    case "volume_spike": {
      const token = getRandomToken();
      const volumeIncrease = Math.floor(Math.random() * 150 + 50);
      return {
        ...baseEvent,
        type: "volume_spike",
        tokens: [token.symbol],
        description: `${volumeIncrease}% surge in ${token.symbol} trading volume`,
        data: {
          volumeIncrease,
          volumeUSD: Math.floor(Math.random() * 1000000) + 100000,
        },
      };
    }

    case "milestone": {
      const totalVolume = raceData.totalVolume;
      const milestone = Math.ceil(totalVolume / 1000) * 1000;
      return {
        ...baseEvent,
        type: "milestone",
        tokens: [],
        description: `Race reaches $${(milestone / 1000).toFixed(
          1
        )}K in total volume`,
        data: {
          milestone,
          type: "volume",
        },
      };
    }

    case "support_level": {
      const token = getRandomToken();
      const level = token.currentPrice;
      const type = Math.random() > 0.5 ? "support" : "resistance";
      return {
        ...baseEvent,
        type: "support_level",
        tokens: [token.symbol],
        description: `${token.symbol} breaks key ${type} at $${level.toFixed(
          2
        )}`,
        data: {
          level,
          type,
          token: token.symbol,
        },
      };
    }

    default:
      return null;
  }
};

// Generate play by play history for a race
const generatePlayByPlayHistory = (raceData) => {
  const elapsedMs = Date.now() - new Date(raceData.startTime).getTime();
  const elapsedMinutes = Math.floor(elapsedMs / (1000 * 60));

  // Generate events (1 event every 2-5 minutes on average)
  const numberOfEvents = Math.floor(elapsedMinutes / (Math.random() * 3 + 2));

  let events = [];
  for (let i = 0; i < numberOfEvents; i++) {
    const minutesAgo = Math.floor(Math.random() * elapsedMinutes);
    const event = generatePlayByPlayEvent(raceData, minutesAgo);
    if (event) events.push(event);
  }

  // Sort by timestamp (most recent first)
  return events.sort((a, b) => b.timestamp - a.timestamp);
};

// Modified to work with real-time data
const generateParticipants = (tokens) => {
  let participants = [];
  tokens.forEach((token) => {
    const participantCount = Math.floor(Math.random() * 3) + 2;
    for (let i = 0; i < participantCount; i++) {
      const walletAddress = generateWalletAddress();
      participants.push({
        address: formatWalletAddress(walletAddress),
        avatar: `https://api.dicebear.com/7.x/identicon/svg?seed=${walletAddress}`,
        bet: Math.floor(Math.random() * 5000) + 500,
        token: token.symbol,
        joinedAgo: Math.floor(Math.random() * 60) + " minutes ago",
        position: token.position || Math.floor(Math.random() * 100) + 1,
      });
    }
  });
  return participants.sort((a, b) => b.bet - a.bet);
};

// Add new formatting helpers
const formatPercentage = (value) => {
  if (typeof value !== "number") return "0.00";
  const fixedValue = value.toFixed(2);
  return fixedValue === "-0.00" ? "0.00" : fixedValue;
};

const formatPrice = (price) => {
  if (typeof price !== "number") return "0.00";

  if (price >= 1) {
    return price.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  } else {
    return price.toLocaleString(undefined, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    });
  }
};

const formatTokenData = (symbol, realTimePrice, tokenData, startPrice) => {
  const currentPrice = realTimePrice?.price || tokenData.price;

  const baseStartPrice = startPrice || tokenData.price;
  const numericPercentChange = startPrice
    ? ((currentPrice - baseStartPrice) / baseStartPrice) * 100
    : 0;

  return {
    symbol,
    name: tokenData.name,
    startPrice: baseStartPrice,
    currentPrice,
    formattedPrice: formatPrice(currentPrice),

    // Keep percentChange numeric:
    percentChange: parseFloat(numericPercentChange.toFixed(2)),

    // If you still want a nicely formatted string somewhere else, store it separately:
    percentChangeFormatted: formatPercentage(numericPercentChange),

    rawPercentChange: numericPercentChange,
    position: 0,
    poolSize: Math.floor(Math.random() * 5000) + 1000,
    odds: parseFloat((Math.random() * 10 + 2).toFixed(2)),
  };
};

const generateLiveData = () => {
  const raceTypes = [
    {
      id: "live1",
      title: "Blue Chip Sprint",
      tokens: ["BTC", "ETH", "SOL"],
      type: "marketCap",
      handicap: "Market Cap Weighted",
    },
    {
      id: "live2",
      title: "DeFi Dynasty",
      tokens: ["UNI", "LINK", "MATIC"],
      type: "defi",
      handicap: "Liquidity Adjusted",
    },
    {
      id: "live3",
      title: "Altcoin Arena",
      tokens: ["ADA", "AVAX", "SOL"],
      type: "altcoin",
      handicap: "Volume Weighted",
    },
  ];

  return raceTypes.map((raceType) => {
    const startTimeOffset = -1 * 3600000; // Always 1 hour ago
    const startTime = new Date(Date.now() + startTimeOffset);
    const endTime = new Date(startTime.getTime() + 24 * 3600000);

    const elapsedMs = Date.now() - startTime.getTime();
    const remainingMs = endTime.getTime() - Date.now();

    // Create tokens with real-time data
    const tokens = raceType.tokens.map((symbol) => {
      const realTimePrice = realTimeDataService.priceData.get(symbol);
      const tokenData = baseTokens[symbol];
      const startPrice = realTimeDataService.startPrices.get(
        `${raceType.id}-${symbol}`
      );

      return formatTokenData(symbol, realTimePrice, tokenData, startPrice);
    });

    // Sort tokens by actual percentage change
    tokens.sort((a, b) => b.rawPercentChange - a.rawPercentChange);
    tokens.forEach((token, index) => {
      token.position = index + 1;
    });

    const participants = generateParticipants(tokens);

    // Create the race data object
    const returnData = {
      id: raceType.id,
      title: raceType.title,
      status: "live",
      elapsedTime: formatDuration(elapsedMs),
      remainingTime: formatDuration(remainingMs),
      totalPrizePool: Math.floor(Math.random() * 20000) + 10000,
      tokens,
      participants,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      handicap: raceType.handicap,
      type: raceType.type,
      lastUpdate: new Date().toISOString(),
      totalParticipants: participants.length,
      totalVolume: participants.reduce((sum, p) => sum + p.bet, 0),
    };

    // Add play-by-play events after creating the race data object
    returnData.playByPlay =
      realTimeDataService.activeRaces.get(raceType.id)?.playByPlay ||
      generatePlayByPlayHistory(returnData);

    return returnData;
  });
};

export const getLiveRaces = async () => {
  try {
    const allSymbols = [
      ...new Set(["BTC", "ETH", "SOL", "UNI", "LINK", "MATIC", "ADA", "AVAX"]),
    ];

    // In getLiveRaces()
    // In getLiveRaces() in watchServices.js
    if (!realTimeDataService.websocket) {
      await realTimeDataService.initialize(allSymbols);
      // Wait for connection
      await new Promise((resolve) => {
        const unsubscribe = realTimeDataService.subscribeToConnection(
          (status) => {
            if (status.status === "connected") {
              unsubscribe();
              resolve();
            }
          }
        );
      });

      const liveRaces = generateLiveData();
      // Need to use Promise.all since startRace is now async
      await Promise.all(
        liveRaces.map((race) => realTimeDataService.startRace(race))
      );
    }

    const races = generateLiveData();
    return races;
  } catch (error) {
    console.error("Error in getLiveRaces:", error);
    return generateLiveData();
  }
};

// Keep existing exports
export const getLiveRaceById = async (id) => {
  const races = await getLiveRaces();
  return races.find((race) => race.id === id);
};

export const getLiveRacesByType = async (type) => {
  const races = await getLiveRaces();
  return type === "all" ? races : races.filter((race) => race.type === type);
};

export const getAvailableRaceTypes = () => [
  { id: "all", label: "All Races" },
  { id: "marketCap", label: "Market Cap" },
  { id: "defi", label: "DeFi" },
  { id: "altcoin", label: "Altcoins" },
];

// Export helpers including the new formatting functions
export const helpers = {
  formatDuration,
  formatWalletAddress,
  generateWalletAddress,
  formatPlayByPlayTime,
  formatPrice,
  formatPercentage,
};
